import { z } from 'zod';

import { Currency } from '../enums';
import { EarningsGranularity } from '../types/trainer-earnings';
import {
  isOrderedPerformanceRange,
  isWithinPerformanceRange,
  PerformanceRangeShape,
} from './performance';

/** The page cap of the trainer earnings line items. */
export const TRAINER_EARNINGS_ITEMS_PAGE_MAX = 200;

const EARNINGS_GRANULARITIES = [
  'week',
  'month',
] as const satisfies readonly EarningsGranularity[];

/**
 * MV2-577 Part 2 (3.4) —
 * `GET organizations/:organizationId/performance/trainer-earnings`.
 *
 * The Performance range (bare, inclusive civil days of the ORGANIZATION's
 * zone, at most `PERFORMANCE_RANGE_MAX_DAYS`), the bucket size and an
 * optional currency filter. `.strict()` makes a smuggled
 * `organizationId`/`tenantId` a refusal, never an override: the tenant is the
 * caller's resolved one and the organization comes from the path.
 */
export const TrainerEarningsQueryDto = z
  .object({
    ...PerformanceRangeShape,
    currency: z.nativeEnum(Currency).optional(),
    granularity: z.enum(EARNINGS_GRANULARITIES).default('month'),
  })
  .strict()
  .refine(isOrderedPerformanceRange, {
    message: 'VALIDATION__EndBeforeStart',
    path: ['to'],
  })
  .refine(isWithinPerformanceRange, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });

export type ApiTrainerEarningsQueryDto = z.infer<
  typeof TrainerEarningsQueryDto
>;

/**
 * `GET organizations/:organizationId/performance/trainer-earnings/items` —
 * one trainer's fee lines over the same range, a page at a time. The page
 * cap and the range cap together bound the read (the lines of one trainer
 * over the whole window are paged on the server).
 */
export const TrainerEarningsItemsQueryDto = z
  .object({
    ...PerformanceRangeShape,
    currency: z.nativeEnum(Currency).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(TRAINER_EARNINGS_ITEMS_PAGE_MAX)
      .default(50),
    offset: z.coerce.number().int().min(0).default(0),
    trainerId: z.string().uuid(),
  })
  .strict()
  .refine(isOrderedPerformanceRange, {
    message: 'VALIDATION__EndBeforeStart',
    path: ['to'],
  })
  .refine(isWithinPerformanceRange, {
    message: 'VALIDATION__InvalidRange',
    path: ['to'],
  });

export type ApiTrainerEarningsItemsQueryDto = z.infer<
  typeof TrainerEarningsItemsQueryDto
>;
